import React, { useEffect, useState } from "react";
import LiquidGlass from "./LiquidGlass";
import GlassTrack from "./GlassTrack";
import { NowPlayingExpanded, NowPlayingTile } from "./NowPlaying";
import usePlayer, { player } from "../hooks/usePlayer";
import useWifi, { wifi, wifiStatus } from "../hooks/useWifi";
import { notify } from "./Notification";
import { ReactComponent as Wifi } from "../assets/svgs/sf-symbols/control-center/wifi.svg";
import { ReactComponent as WifiOff } from "../assets/svgs/sf-symbols/control-center/wifi.slash.svg";
import { ReactComponent as Bluetooth } from "../assets/svgs/sf-symbols/control-center/bluetooth.svg";
import { ReactComponent as BluetoothOff } from "../assets/svgs/sf-symbols/control-center/bluetooth.slash.svg";
import { ReactComponent as AirDrop } from "../assets/svgs/sf-symbols/control-center/airdrop.svg";
import { ReactComponent as AirDropOff } from "../assets/svgs/sf-symbols/control-center/airdrop.slash.svg";
import { ReactComponent as Moon } from "../assets/svgs/sf-symbols/control-center/moon.fill.svg";
import { ReactComponent as StageManager } from "../assets/svgs/sf-symbols/control-center/squares.leading.rectangle.svg";
import { ReactComponent as ScreenMirroring } from "../assets/svgs/sf-symbols/control-center/rectangle.on.rectangle.svg";
import { ReactComponent as Appearance } from "../assets/svgs/sf-symbols/control-center/circle.lefthalf.filled.svg";
import { ReactComponent as Screenshot } from "../assets/svgs/sf-symbols/control-center/camera.viewfinder.svg";
import { ReactComponent as SunMin } from "../assets/svgs/sf-symbols/control-center/sun.min.fill.svg";
import { ReactComponent as SunMax } from "../assets/svgs/sf-symbols/control-center/sun.max.fill.svg";
import { ReactComponent as SpeakerMin } from "../assets/svgs/sf-symbols/control-center/speaker.fill.svg";
import { ReactComponent as SpeakerMax } from "../assets/svgs/sf-symbols/control-center/speaker.wave.3.fill.svg";
import { ReactComponent as AirPlayAudio } from "../assets/svgs/sf-symbols/control-center/airplayaudio.svg";
import { ReactComponent as Chevron } from "../assets/svgs/sf-symbols/control-center/chevron.right.svg";

// A white screen flash, like taking a screenshot.
const flashScreen = () => {
  const el = document.createElement("div");
  el.className = "screen-flash";
  el.addEventListener("animationend", () => el.remove());
  document.body.appendChild(el);
};

// 2-wide capsule: a disc that turns white when on, then a title and status.
const Capsule = ({ on, onClick, icon, title, status, tint = "blue", chevron, index }) => (
  <LiquidGlass
    as="button"
    type="button"
    onClick={onClick}
    aria-pressed={on}
    className="cc-capsule cc-item"
    style={{ "--i": index }}
  >
    <span className={`cc-disc ${on ? `is-on is-${tint}` : ""}`}>{icon}</span>
    <span className="min-w-0 flex-1">
      <span className="cc-title">{title}</span>
      {status && <span className="cc-subtitle">{status}</span>}
    </span>
    {chevron && <Chevron className="cc-chevron" aria-hidden="true" />}
  </LiquidGlass>
);

// 1x1 round control.
const Circle = ({ on, onClick, icon, label, index }) => (
  <LiquidGlass
    as="button"
    type="button"
    onClick={onClick}
    aria-pressed={on}
    aria-label={label}
    title={label}
    className={`cc-circle cc-item ${on ? "is-on" : ""}`}
    style={{ "--i": index }}
  >
    {icon}
  </LiquidGlass>
);

// Full-width slider: thin track between a min and max glyph.
const SliderModule = ({ label, value, onChange, MinIcon, MaxIcon, accessory, index }) => (
  <LiquidGlass radius={26} className="cc-slider-module cc-item" style={{ "--i": index }}>
    <p className="cc-title">{label}</p>
    <div className="cc-slider-row">
      <MinIcon className="cc-slider-glyph cc-slider-glyph--min" aria-hidden="true" />
      <GlassTrack value={value} onChange={onChange} label={label} />
      <MaxIcon className="cc-slider-glyph cc-slider-glyph--max" aria-hidden="true" />
      {accessory}
    </div>
  </LiquidGlass>
);

// Sound drives the shared player's volume. It subscribes on its own so the rest of
// Control Center doesn't re-render as the song plays.
const SoundModule = ({ index }) => {
  const { volume } = usePlayer();
  return (
    <SliderModule
      label="Sound"
      value={Math.round(volume * 100)}
      onChange={(v) => player.setVolume(v / 100)}
      MinIcon={SpeakerMin}
      MaxIcon={SpeakerMax}
      accessory={
        <span className="cc-airplay" aria-hidden="true">
          <AirPlayAudio />
        </span>
      }
      index={index}
    />
  );
};

const ControlCenter = ({ open }) => {
  const [display, setDisplay] = useState(50);
  const [expanded, setExpanded] = useState(false);
  const [disturbMode, toggleDisturb] = useState(false);
  const wifiState = useWifi();
  const [bluetoothMode, toggleBluetooth] = useState(true);
  const [airdropMode, toggleAirdrop] = useState(true);
  const [darkMode, setDarkMode] = useState(false);

  const [stageMode, setStageMode] = useState(false);
  const [screenMirroring, setScreenMirroring] = useState(false);

  // Closing Control Center returns it to the grid for next time.
  useEffect(() => {
    if (open) return undefined;
    const id = setTimeout(() => setExpanded(false), 250);
    return () => clearTimeout(id);
  }, [open]);

  // Like macOS, the expanded view has no back button: Escape or a click on empty space returns.
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => e.key === "Escape" && setExpanded(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  return (
    <div
      className={`cc-layout ${expanded ? "is-expanded" : ""}`}
      onClick={(e) => expanded && e.target === e.currentTarget && setExpanded(false)}
    >
      <div className="cc-grid">
        <Capsule
          on={wifiState.on}
          onClick={wifi.toggle}
          icon={wifiState.on ? <Wifi /> : <WifiOff />}
          title="Wi-Fi"
          status={wifiStatus(wifiState)}
          index={0}
        />
        <LiquidGlass radius={30} className="cc-tile cc-item" style={{ "--i": 1 }}>
          <NowPlayingTile onExpand={() => setExpanded(true)} />
        </LiquidGlass>
        <Capsule
          on={bluetoothMode}
          onClick={() => toggleBluetooth(!bluetoothMode)}
          icon={bluetoothMode ? <Bluetooth /> : <BluetoothOff />}
          title="Bluetooth"
          status={bluetoothMode ? "On" : "Off"}
          chevron
          index={2}
        />
        <Capsule
          on={airdropMode}
          onClick={() => toggleAirdrop(!airdropMode)}
          icon={airdropMode ? <AirDrop /> : <AirDropOff />}
          title="AirDrop"
          status={airdropMode ? "Contacts Only" : "Off"}
          index={3}
        />
        <Circle
          on={stageMode}
          onClick={() => setStageMode(!stageMode)}
          icon={<StageManager />}
          label="Stage Manager"
          index={4}
        />
        <Circle
          on={screenMirroring}
          onClick={() => setScreenMirroring(!screenMirroring)}
          icon={<ScreenMirroring />}
          label="Screen Mirroring"
          index={5}
        />
        <Circle
          on={darkMode}
          onClick={() => setDarkMode(!darkMode)}
          icon={<Appearance />}
          label="Dark Mode"
          index={6}
        />
        <Circle on={false} onClick={flashScreen} icon={<Screenshot />} label="Screenshot" index={7} />
        <Capsule
          on={disturbMode}
          onClick={() => toggleDisturb(!disturbMode)}
          icon={<Moon />}
          title={disturbMode ? "Do Not Disturb" : "Focus"}
          tint="indigo"
          index={8}
        />
        <SliderModule
          label="Display"
          value={display}
          onChange={setDisplay}
          MinIcon={SunMin}
          MaxIcon={SunMax}
          index={9}
        />
        <SoundModule index={10} />
      </div>

      <LiquidGlass
        as="button"
        type="button"
        onClick={() => notify("Editing controls is turned off on this Mac. Open Notes to read my resume.")}
        className="cc-edit cc-item"
        style={{ "--i": 11 }}
      >
        Edit Controls
      </LiquidGlass>

      <LiquidGlass radius={24} className="cc-expanded" aria-hidden={!expanded}>
        <NowPlayingExpanded />
      </LiquidGlass>
    </div>
  );
};

export default ControlCenter;
